// Device commands — the /v1/api/cmd/* "regCmd" handlers (Go side:
// cmd/noc-app/cmdapi.go). These dispatch directly to noc-link. Add a
// wrapper here per command you call from the UI.
//
// All return {ok: boolean, error?: string} (200 on success, 502 with
// {ok:false,error} on a device-side failure — ApiClient turns the latter
// into an ApiError).

import type { ApiClient } from "./client.js";

export interface CmdResult {
  ok: boolean;
  error?: string;
}

/** GET /v1/api/cmd/ifrate — live TX/RX bitrate + packet-rate of a subscriber's
 *  PPPoE interface, from the router's perspective: `rx_*` = router received =
 *  subscriber **upload**; `tx_*` = router transmitted = subscriber **download**.
 *  Bits per second for `_bps`, packets per second for `_pps`. */
export interface IfRateResult {
  ok: boolean;
  rx_bps?: number;
  tx_bps?: number;
  rx_pps?: number;
  tx_pps?: number;
  error?: string;
}

/** GET /v1/api/cmd/onu_detail — a fresh over-SSH read of one ONU's full
 *  optical/hardware/LAN record (the poller's cached snapshot only has the
 *  identity + online flag). `onu` is the same pass-through blob shape as
 *  /v1/api/search's `onu` (internal/olt/types.go ONURecord). */
export interface OnuDetailResult {
  ok: boolean;
  onu?: Record<string, unknown>;
  error?: string;
}

/** POST /v1/api/cmd/onu_rx — a fresh SNMP sweep of the ONUs for a set of
 *  subscribers; `powers` is {username → rx_power_dbm} (e.g. "-23.4"), with
 *  an entry omitted for any user whose ONU couldn't be polled this round. */
export interface OnuRxResult {
  ok: boolean;
  powers?: Record<string, string>;
  error?: string;
}

/** One reply line from a /ping run (mikrotik.PingEntry). */
export interface PingEntry {
  seq: number;
  host?: string;
  time_ms?: number;
  ttl?: number;
  size?: number;
  status: string; // "ok" | "timeout"
}
/** A completed /ping run from a router (mikrotik.PingReply). */
export interface PingReport {
  router: string;
  address: string;
  sent: number;
  received: number;
  loss_pct: number;
  avg_ms?: number;
  min_ms?: number;
  max_ms?: number;
  size: number;
  ttl?: number;
  interval_s?: number;
  replies: PingEntry[];
}
export interface PingResult {
  ok: boolean;
  result?: PingReport;
  error?: string;
}
export interface PingArgs {
  router: string;
  address: string;
  count?: number; // 1..20, default 10
  size?: number; // 16..1500, 0 = router default (56)
  ttl?: number; // 1..255, 0 = router default (64)
  interval_ms?: number; // 100..5000, default 100
  df?: boolean; // "do not fragment" flag
}

/** One destination IP from a /tool torch capture (mikrotik.TorchEntry).
 *  Field names mirror RouterOS's router-perspective on the PPPoE interface:
 *  `tx_bytes` = router transmitted → customer **download**;
 *  `rx_bytes` = router received    ← customer **upload**.
 *  Presentation code (TorchDialog) flips them for the customer-facing labels. */
export interface TorchEntry {
  dst_ip: string;
  tx_bytes: number;
  rx_bytes: number;
}
export interface TorchResult {
  ok: boolean;
  entries?: TorchEntry[];
  sec?: number;
  error?: string;
}

/** POST /v1/api/cmd/olt_write — save running-config to NVRAM on every OLT.
 *  `results` maps OLT name → "ok" or an error string. */
export interface OltWriteResult {
  ok: boolean;
  results?: Record<string, string>;
  error?: string;
}

export function makeCmdApi(client: ApiClient) {
  return {
    /** Trigger a clean restart of noc-app (cmd-api + SSE + jobs);
     *  systemd brings it back in ~1s. noc-link (device connections) is a
     *  separate unit and unaffected. Admin only — the server gates this
     *  on portal_users.role = 'admin'. Responds 200 immediately, then
     *  exits ~50ms later, so the SSE stream drops and reconnects. */
    restart(): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/restart", {});
    },
    /** Set (or clear, when `username` is "") an ONU's description on its OLT —
     *  i.e. bind / unbind a PPPoE username to a registered ONU. */
    oltAssign(args: { olt_name: string; onu_name: string; username: string }): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/olt_assign", args);
    },
    /** Delete an ONU registration entirely (destructive — the ONU drops off
     *  the bus until rebound). Not the same as clearing the description. */
    oltRemove(args: { olt_name: string; onu_name: string }): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/olt_remove", args);
    },
    /** Set the desc on many ONUs of one OLT in a single Telnet session
     *  (the OLT Issues "Auto Add" batch). `username` is the desc target —
     *  a PPPoE username or a dash-form switch name. */
    oltBatchAssign(args: {
      olt_name: string;
      assignments: { onu_name: string; username: string }[];
    }): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/olt_batch_assign", args);
    },
    /** Clear the desc on many ONUs of one OLT (the OLT Issues per-row
     *  "Remove" and the "Remove Binding" batch). The ONU stays registered. */
    oltBatchClearDesc(args: { olt_name: string; onu_names: string[] }): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/olt_batch_clear_desc", args);
    },
    /** Suppress an OLT-issue row from reappearing before the next recompute
     *  confirms it's resolved (fire after an assign/clear). */
    oltTombstone(args: { olt_name: string; onu_names: string[] }): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/olt_tombstone", args);
    },
    /** Mark RADIUS issues for these usernames resolved — a 5-min UI
     *  tombstone (`radius:resolved:<user>`) so the row doesn't reappear
     *  before the detector's next pass confirms the fix. */
    radiusTombstone(usernames: string[]): Promise<CmdResult> {
      return client.post<CmdResult>("/v1/api/cmd/radius_tombstone", { usernames });
    },
    /** Save running-config to NVRAM on every OLT. */
    oltWrite(): Promise<OltWriteResult> {
      return client.post<OltWriteResult>("/v1/api/cmd/olt_write", {});
    },
    /** Live TX/RX bitrate for a subscriber's session on a router. */
    ifRate(router: string, username: string): Promise<IfRateResult> {
      return client.get<IfRateResult>("/v1/api/cmd/ifrate", { router, username });
    },
    /** Fresh over-SSH read of an ONU's detail (optical, hardware, LAN,
     *  uptime). Slower than the cached snapshot — call on demand. `name` is
     *  the ONU desc (a subscriber's username, or a switch box's hyphenated
     *  name). Pass `olt` for a non-subscriber ONU (a switch box) so a
     *  reseller granted that OLT may read it — admin/support ignore it. */
    onuDetail(name: string, olt?: string): Promise<OnuDetailResult> {
      return client.get<OnuDetailResult>("/v1/api/cmd/onu_detail", olt ? { username: name, olt } : { username: name });
    },
    /** Fresh SNMP sweep of the ONUs for a list of subscribers → live RX-power
     *  readings. Bounded (~ up to 2 min worst case for a big sweep). */
    onuRx(usernames: string[]): Promise<OnuRxResult> {
      return client.post<OnuRxResult>("/v1/api/cmd/onu_rx", { usernames });
    },
    /** Run /ping from a router toward an address. Takes a few seconds. */
    ping(args: PingArgs): Promise<PingResult> {
      const { df, ...rest } = args;
      return client.post<PingResult>("/v1/api/cmd/ping", { ...rest, df: df ? 1 : 0 });
    },
    /** Capture per-destination traffic on a subscriber's PPPoE interface
     *  for `sec` seconds (5..60). Blocks for roughly that long. */
    torch(router: string, username: string, sec = 30): Promise<TorchResult> {
      return client.get<TorchResult>("/v1/api/cmd/torch", { router, username, sec });
    },
  };
}

export type CmdApi = ReturnType<typeof makeCmdApi>;
